import React, { useCallback, useMemo } from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  FontWeight,
  Image,
  JustifyContent,
  Overflow,
  Space,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { boxSummaryVisibilityChange } from 'actions/boxSummary'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useMinRecipesForPortion } from 'hooks/menu-boxes/useMinRecipesForPortion'
import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { registerBoxSummaryTreatmentFocusTarget } from 'routes/Menu/components/BoxSummary/treatmentFocus'

import stackedRecipesImage from './assets/stacked-recipes.jpg'

type VisualBasketRecipe = {
  id: RecipeUUID
  imageUrl?: string
  key: string
  name: string
}

const ClickableBasket = withCitrusTheme(Box, () => ({
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  '&:focus-visible': {
    outline: `2px solid ${Color.White}`,
    outlineOffset: '2px',
  },
}))

const BasketPrompt = ({
  maxRecipesInBasket,
  minRecipesInBasket,
}: {
  maxRecipesInBasket: number
  minRecipesInBasket: number
}) => (
  <Box
    data-testid="visual-basket-prompt"
    alignItems={AlignItems.Center}
    borderColor={Color.ColdGrey_200}
    borderStyle={BorderStyle.Dashed}
    borderWidth={0.5}
    display={Display.Flex}
    flexGrow={[1, null, 0]}
    height="64px"
    paddingH={3}
    width="auto"
  >
    <Image
      data-testid="visual-basket-prompt-image"
      alt="recipe images stacked on top of each other"
      height="45px"
      src={stackedRecipesImage}
      width="48px"
    />
    <Space direction="horizontal" size={2} />
    <Box>
      <Text color={Color.White} display={Display.Block} fontWeight={FontWeight.Bold} size={2}>
        {`Add ${minRecipesInBasket} to ${maxRecipesInBasket} recipes you like`}
      </Text>
      <Text color={Color.White} display={Display.Block} size={2}>
        More recipes, better value.
      </Text>
    </Box>
  </Box>
)

export const VisualBasket = () => {
  const dispatch = useDispatch()
  const { basketRecipes } = useBasketRecipes()
  const { numPortions } = useNumPortions()
  const maxRecipesInBasket = useMaxRecipesForPortion(numPortions)
  const minRecipesInBasket = useMinRecipesForPortion(numPortions)

  const selectedRecipes = useMemo<VisualBasketRecipe[]>(() => {
    const recipes: VisualBasketRecipe[] = []

    basketRecipes.forEach(({ recipe, quantity }) => {
      if (!recipe) {
        return
      }

      const imageCrops =
        recipe.images?.find(({ type }) => type === 'mood-image')?.crops || recipe.images?.[0]?.crops

      const imageUrl = imageCrops?.find(({ width }) => width === 200)?.url || imageCrops?.[0]?.url

      recipes.push(
        ...Array.from({ length: quantity }, (_, recipeCount) => ({
          id: recipe.id,
          imageUrl,
          key: `${recipe.id}-${recipeCount}`,
          name: recipe.name,
        })),
      )
    })

    return recipes.slice(0, maxRecipesInBasket)
  }, [basketRecipes, maxRecipesInBasket])

  const shouldShowEmptyPlaceholder = selectedRecipes.length === 0
  const shouldShowPrompt = selectedRecipes.length < minRecipesInBasket

  // The tray is one button, so its accessible name conveys what the prompt /
  // recipe images show sighted users: the "add more" guidance or the count.
  const basketLabel = shouldShowPrompt
    ? `Open your basket. Add ${minRecipesInBasket} to ${maxRecipesInBasket} recipes you like for the best value.`
    : `Open your basket, ${selectedRecipes.length} ${
        selectedRecipes.length === 1 ? 'recipe' : 'recipes'
      } added.`

  const openOrderSummary = useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      // Stop the click reaching the (also clickable) banner, which would open
      // and track the box summary a second time.
      event.stopPropagation()

      // Register the tray so focus returns to it when the Order Summary closes.
      if (event.currentTarget instanceof HTMLElement) {
        registerBoxSummaryTreatmentFocusTarget(event.currentTarget)
      }

      dispatch(boxSummaryVisibilityChange(true))
    },
    [dispatch],
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openOrderSummary(event)
    }
  }

  return (
    <ClickableBasket
      data-testid="visual-basket"
      aria-label={basketLabel}
      role="button"
      tabIndex={0}
      onClick={openOrderSummary}
      onKeyDown={handleKeyDown}
      alignItems={AlignItems.Center}
      display={Display.Flex}
      gap={2}
      justifyContent={JustifyContent.FlexStart}
      width={['100%', null, 'auto']}
    >
      {shouldShowEmptyPlaceholder && (
        <Box
          data-testid="visual-basket-placeholder-slot"
          bg={Color.ColdGrey_100}
          borderRadius={1}
          flexShrink={0}
          height="64px"
          minWidth="64px"
          overflowX={Overflow.Hidden}
          overflowY={Overflow.Hidden}
          width="64px"
        />
      )}

      {selectedRecipes.map((recipe) => {
        return (
          <Box
            data-testid="visual-basket-recipe-slot"
            key={recipe.key}
            borderRadius={1}
            flexShrink={0}
            height="64px"
            minWidth="64px"
            overflowX={Overflow.Hidden}
            overflowY={Overflow.Hidden}
            width="64px"
          >
            {recipe.imageUrl ? (
              <Image
                data-testid="visual-basket-recipe-image"
                alt={recipe.name}
                fit="cover"
                height="100%"
                src={recipe.imageUrl}
                width="100%"
              />
            ) : null}
          </Box>
        )
      })}

      {shouldShowPrompt && (
        <BasketPrompt
          maxRecipesInBasket={maxRecipesInBasket}
          minRecipesInBasket={minRecipesInBasket}
        />
      )}
    </ClickableBasket>
  )
}
